import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getYouTubeAuthStatus,
  getYouTubeAuthUrl,
  disconnectYouTube,
  type YouTubeAuthStatus,
} from './api';

/**
 * YouTube 認証完了の postMessage / localStorage を監視する hook。
 * (Canva の useCanvaAuthListener と同型)
 */
export function useYouTubeAuthListener(onAuthSuccess?: () => void) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'YOUTUBE_AUTH_SUCCESS') {
        queryClient.invalidateQueries({ queryKey: ['youtube', 'authStatus'] });
        onAuthSuccess?.();
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'youtube_auth_complete' && event.newValue) {
        queryClient.invalidateQueries({ queryKey: ['youtube', 'authStatus'] });
        localStorage.removeItem('youtube_auth_complete');
        onAuthSuccess?.();
      }
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [queryClient, onAuthSuccess]);
}

/** YouTube 連携ステータスを取得する hook */
export function useYouTubeAuthStatus() {
  return useQuery<YouTubeAuthStatus>({
    queryKey: ['youtube', 'authStatus'],
    queryFn: getYouTubeAuthStatus,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** YouTube 連携を解除する hook */
export function useDisconnectYouTube() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectYouTube,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube', 'authStatus'] });
    },
  });
}

/** YouTube 認証を開始する hook (マイページ用) */
export function useYouTubeAuth() {
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const startAuth = useCallback(async () => {
    // ポップアップがブロックされ同タブ遷移にフォールバックした場合に、認証完了後
    // 元のページへ戻れるよう現在地を控える (auth-complete ページが参照)。
    try {
      sessionStorage.setItem('youtube_return_to', window.location.pathname + window.location.search);
    } catch {
      /* noop */
    }
    // Safari のポップアップブロック対策: ユーザー操作と同期的に新規タブを開く
    const targetWindow = window.open('about:blank', '_blank');
    setIsAuthenticating(true);
    try {
      const { url } = await getYouTubeAuthUrl();
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to get YouTube auth URL:', error);
      if (targetWindow && !targetWindow.closed) {
        targetWindow.close();
      }
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  return { startAuth, isAuthenticating };
}
